<script lang="ts">
  import type { FeatureTracking, FeatureTrackingReference } from "../tracking";
  import MinMaxInput from "./custom-range/MinMaxInput.svelte";
  import SliderInput from "./custom-range/SliderInput.svelte";
  import type { Interval, FacetValue, FacetValueOptions } from "./type";
  import { SingleSelect } from "../facet-value-input";
  import { updateFeatureTrackingLabel } from "../tracking/FeatureTracking.ts";
  import { initToggles, isActive } from "../../toggle/Toggle.ts";

  interface Props extends FacetValue, FacetValueOptions {
    selectedValues?: string;
    featureTracking?: FeatureTracking;
    availableRange?: Required<Interval>;
    unit?: string;
    logarithmic?: boolean;
    useMinMax?: boolean;
    useSlider?: boolean;
    excludeFromTracking?: boolean;
    filterId?: string;
    popularValues?: FacetValue[];
  }

  let {
    id,
    name,
    checked = $bindable(false),
    selectedValues = $bindable(),
    range = $bindable({}),
    featureTracking,
    availableRange,
    unit,
    logarithmic = false,
    useMinMax = true,
    useSlider = false,
    excludeFromTracking = $bindable(false),
    filterId,
    popularValues,
  }: Props = $props();

  /*                                              */
  initToggles();
  const preventInvalidSelections = isActive("HEUREKA_2208_PREVENT_SUBMISSION_OF_INVALID_SELECTIONS");

  let value = $derived(`${range?.from || "_"},${range?.to || "_"}`);
  let rangeIsValid = $derived(
    !range?.from ||
      !range?.to ||
      (range?.from ?? 0) <= (range?.to ?? 0) ||
      (range?.from === undefined && range?.to === undefined),
  );
  let validityOnChange = $state(true);
  let isValid = $derived(preventInvalidSelections ? validityOnChange : rangeIsValid);

  function oninput() {
    setTimeout(() => {
      const event = new CustomEvent("heureka.customRange.onInput", {
        bubbles: true,
        detail: { filterId },
      });
      document.dispatchEvent(event);
    }, 0);
    checked = true;
    excludeFromTracking = false;
  }

  function onchange() {
    if (popularValues && popularValues.length > 0 && featureTracking) {
      const equalsTopFacetValue = !!popularValues.find(
        (popVal) => popVal.range?.from === range?.from && popVal.range?.to === range?.to,
      );
      featureTracking = updateFeatureTrackingLabel(
        featureTracking,
        "san_TopFacetValue",
        equalsTopFacetValue ? "popular" : "none",
      );
    }
    if (preventInvalidSelections) {
      validityOnChange = rangeIsValid;
    }
    /*                                                                                  */
    setTimeout(() => {
      const event = new CustomEvent("heureka.customRange.validityChanged", {
        bubbles: true,
        detail: { filterId },
      });
      document.dispatchEvent(event);
    }, 0);
  }

  function resetRange() {
    range.from = undefined;
    range.to = undefined;
    if (preventInvalidSelections) {
      validityOnChange = true;
    }
  }

  document.addEventListener("heureka.filterMessageBanner.dismiss", (event) => {
    const customEvent = event as CustomEvent<{ filterId: string }>;
    const customEventFilterId = customEvent.detail?.filterId;
    /*                                                                   */
    if (customEventFilterId.includes(filterId!) && !isValid && range) {
      resetRange();
    }
  });

  const featureTrackingReference: FeatureTrackingReference = {
    ref: id,
    labels: {},
  };
</script>

<div class="hc_customRange hc_facetValue" data-facet-name={name} data-filter-id={filterId} {oninput} {onchange}>
  <label hidden>
    <SingleSelect
      {id}
      {checked}
      class="hc_customRange__input"
      {name}
      {value}
      bind:selectedValues
      bind:featureTracking
      bind:excludeFromTracking
      {isValid}
    />
  </label>
  {#if useMinMax}
    <MinMaxInput {unit} bind:range {availableRange} {featureTrackingReference} {isValid} />
  {/if}
  {#if useSlider && availableRange}
    <SliderInput bind:range {availableRange} {logarithmic} {featureTrackingReference} />
  {/if}
</div>

<style lang="scss">
  .hc_customRange {
    display: flex;
    flex-direction: column;
    gap: calc(var(--oc-semantic-spacing-150) - var(--oc-semantic-spacing-25));
  }
</style>
